<template>
  <div id="app">
    <div v-if="isProfileLoading" class="loader">
      <img alt="Loading" :src="loaderImg" height="200" width="200" />
    </div>
    <main
      v-else
      class="main-wrapper"
      :class="{ 'main-wrapper--has-alert': alertVisible }">
      <div v-if="alertVisible" class="main-wrapper__alert">
        <p>
          <span class="mi-24px mi-exclamation-triangle"></span>
          <span>
            We will be undergoing a system upgrade on 12th July 2025 from 2:00
            AM (UTC) to 12th July 2025 3:30 PM (UTC) hours. Please avoid using
            the system during this window.

            <!-- Due to the SAP upgrade, invoice search and waiver processes will be
            affected on April 12th from 10:00 AM UTC to 11:30 AM UTC. Please
            avoid using the system during this window. -->

            <!-- Due to "Data Center exit for SAP ECC," the creation and editing of
            Dispute for the ECC invoice will be unavailable from 5:00 PM UTC on
            March 14th to 4:00 PM UTC on March 15th. We apologize for the
            inconvenience and appreciate your understanding. -->

            <!-- This is to notify that OHP Disputes application would be down due to
            Tax Platform (NFTP) Release 2.2 migration on 9th June 2023 from
            11:30 AM (UTC) hours till 11th June 2023 11:30 AM (UTC) hours. -->
          </span>
        </p>
      </div>
      <Header
        :user="user"
        :panel-states="panelStates"
        :custom-icon="getIsIndexer || getIsViewer || getIsEditor"
        :is-full-width="isSFDC"
        report-url="https://app.powerbi.com/groups/me/apps/a0689fab-ed19-4a48-9395-1d1b193410ec/reports/d25ec1d9-1029-46b5-a6c2-4e8de3371638/ReportSection7954333a14ee40a02906"
        @faq-clicked="handleFaqClicked()"
        @signOut="signOut">
        <div v-if="!isUnderMaintenance && !hasError" slot="custom-icon">
          <a
            v-if="getIsIndexer || getIsViewer || getIsEditor"
            v-tooltip="'Live Dashboard Tracker'"
            class="main-wrapper__custom-search-icon"
            href="/dashboard-tracker"
            target="_blank"
            rel="noopener noreferrer"
            title="Live Dashboard">
            <icon name="chart-line-up" size="24"></icon>
          </a>
          <a
            v-if="getIsIndexer"
            v-tooltip="'Create Dispute'"
            class="main-wrapper__custom-icon"
            href="/create-dispute"
            target="_blank"
            rel="noopener noreferrer"
            title="SFDC">
            <icon name="receipt" size="24"></icon>
          </a>
          <span
            v-if="getIsIndexer"
            v-tooltip="'Search bulk Id to get status'"
            class="main-wrapper__custom-search-icon"
            @click="toggleBulkStatusModal">
            <icon name="magnifying-glass" size="24"></icon>
          </span>
        </div>
      </Header>
      <div v-if="hasError" class="main-content">
        <h4>Unable to load user profile, Please try reloading the page</h4>
      </div>
      <div v-else-if="isUnderMaintenance" class="under-maintenance-wrapper">
        <maintenance />
      </div>
      <div v-else>
        <router-view
          :user="user"
          :is-bulk-status-modal-visible="isBulkStatusModalVisible"
          @toggleBulkStatusModal="toggleBulkStatusModal" />
      </div>

      <Footer :app-version="appVersion" :is-full-width="isSFDC" />
    </main>
  </div>
</template>

<script>
// Assets
import loaderImg from "@/assets/images/man-walking.gif";

// Components
import { showToast } from "@/components/common/Toast";
import Maintenance from "@/pages/Maintenance.vue";

// Constants
import { LOGIN_REQUEST_SCOPES } from "@/config/constant";

// Helpers
import { parseJwt, getMsalTokenSilently } from "@/utils/helpers";
import { PublicClientApplication } from "@azure/msal-browser";
import { mapGetters } from "vuex";

export default {
  components: {
    Maintenance,
  },
  data() {
    return {
      isUnderMaintenance: false,
      alertVisible: false,
      isProfileLoading: true,
      hasError: false,
      loaderImg: loaderImg,
      msalLibrary: {},
      account: {},
      appVersion: process.env.VUE_APP_VERSION,
      isBulkStatusModalVisible: false,
      panelStates: [],
    };
  },
  computed: {
    ...mapGetters(["getIsIndexer"]),
    ...mapGetters(["getIsViewer"]),
    ...mapGetters(["getIsEditor"]),
    user() {
      return this.$store.getters.getUserData;
    },
    isSFDC() {
      const baseUrl = window.location.href.split("/");
      return baseUrl.includes("create-dispute");
    },
  },

  async created() {
    console.log("process.env", process.env.VUE_APP_ENV);
    console.log(this.$store.state.msalConfig);
    this.msalLibrary = new PublicClientApplication(
      this.$store.state.msalConfig
    );
    await this.msalLibrary
      .handleRedirectPromise()
      .then(this.handleResponse)
      .catch((error) => {
        console.log(error);
        this.hasError = true;
        showToast(
          "error",
          "Unable to load user profile, Please try reloading the page"
        );
      });
  },
  methods: {
    toggleBulkStatusModal() {
      this.isBulkStatusModalVisible = !this.isBulkStatusModalVisible;
    },
    async getRoles() {
      try {
        const decodedIdToken = parseJwt(
          this.$store.state.tokenConfiguration.idToken
        );
        const { roles, name, email } = decodedIdToken;
        this.$store.dispatch("setUserDataAction", {
          displayName: name,
          mail: email,
        });
        this.$store.dispatch("setIdTokenAction", decodedIdToken);
        if (roles.length) {
          this.$store.dispatch("setIsEditorAction", roles.includes("editor"));
          this.$store.dispatch("setIsSupportAction", roles.includes("support"));
          this.$store.dispatch("setIsIndexerAction", roles.includes("indexer"));
          this.$store.dispatch("setIsViewerAction", roles.includes("viewer"));
          if (!roles.includes("editor")) {
            showToast(
              "warning",
              "You only have view access. If you want to make changes, please submit a request for editing access."
            );
          }
        }
        this.isProfileLoading = false;
      } catch (err) {
        showToast(
          "error",
          "Unable to load user profile, Please try reloading the page"
        );
      }
    },
    async signIn() {
      return this.msalLibrary
        .loginRedirect(LOGIN_REQUEST_SCOPES)
        .then(this.handleResponse)
        .catch(function (error) {
          console.error(error);
        });
    },
    async signOut() {
      await this.msalLibrary
        .logout({})
        .then(() => {
          localStorage.clear(); // For Clearing Local Storage
          sessionStorage.clear(); // For Clearing Session Storage
          document.cookie.split(";").forEach(function (c) {
            // For Clearing the Cookies
            document.cookie = c
              .replace(/^ +/, "")
              .replace(
                /=.*/,
                "=;expires=" + new Date().toUTCString() + ";path=/"
              );
          });
        })
        .catch((error) => {
          console.error(error);
        });
    },
    async handleResponse(resp) {
      if (resp !== null) {
        this.$store.dispatch("setAccessTokenAction", resp);
        sessionStorage.setItem("msal_token", JSON.stringify(resp));
        this.getRoles();
      } else {
        const currentAccounts = this.msalLibrary.getAllAccounts();
        this.account = currentAccounts[0];
        if (!currentAccounts || currentAccounts.length < 1) {
          this.signIn();
        } else {
          const accountId = currentAccounts[0].homeAccountId;
          const currentAcc = this.msalLibrary.getAccountByHomeId(accountId);
          if (currentAcc) {
            const response = await getMsalTokenSilently(this, {
              ...LOGIN_REQUEST_SCOPES,
              account: currentAccounts[0],
            });
            this.$store.dispatch("setAccessTokenAction", response);
            sessionStorage.setItem("msal_token", JSON.stringify(response));
            this.getRoles();
          }
        }
      }
    },
  },
};
</script>

<style lang="scss">
@import "@/styles/app";
@import "~@maersk/design-system/scss/maeu.scss";
@import "@/styles/palette.scss";

#app {
  & .loader {
    display: flex;
    flex: 1;
    height: 100vh;
    align-items: center;
    justify-content: center;
    background-color: #e3f6fb;
    flex-direction: column;
    & h4 {
      margin: 32px;
    }
  }
  & .main-wrapper {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    &__alert {
      background: var(--Danger-Light);
      display: block !important;
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      height: 64px;
      z-index: 9999;
      & p {
        max-width: 1280px;
        margin: 0 auto;
        display: flex;
        align-items: center;
        height: inherit;
        & span {
          margin-left: 16px;
        }
      }
    }
    &__custom-icon {
      color: var(--Gray-900);
      margin-left: 15px;
    }
    &__custom-search-icon {
      margin-left: 15px;
    }
  }
  & .main-wrapper--has-alert {
    & .ign-header {
      margin-top: 64px;
    }
    & .details {
      margin-top: 80px;
    }
    & .main-content {
      margin-top: 144px;
    }
    & .filter-wrapper {
      & .filter {
        margin-top: 64px;
      }
    }
    & .ign-header__panel--open {
      margin-top: 64px;
    }
  }
}
</style>
